// §4.1/§4.2 floor · SCAN (spec 06 §3.5 "scan-to-locate — the bobbin-location fix").
//
// The motion this screen exists for: scan the bin, scan the bobbin, optionally
// key the measured length, walk away. It is the screen that kills the
// Google-Sheets-plus-WhatsApp-photo loop, so it has to work with a glove on, at
// arm's length, with no signal.
//
// WHAT IS HONESTLY HARD HERE, and how each part is answered:
//
//  * ATTRIBUTION IS SERVER-SIDE. §3.5's ladder rung 1 ("the location QR this
//    device scanned in the last 120 s") is derived from `scan_events` by the
//    SERVER, matched on `device_id` — never client-asserted. So this screen
//    sends the payload plus its stable device id and RENDERS what came back; it
//    never predicts an attribution. Offline, it renders no attribution at all,
//    because it genuinely does not have one (see below).
//  * OFFLINE, A SCAN CANNOT RESOLVE. Resolution needs the server. A queued scan
//    therefore shows "queued — will resolve when you have signal" and NOT a
//    fabricated card. This is the one place a floor screen must refuse to be
//    clever: guessing that `1645` is the bobbin you think it is, from a stale
//    cache, is how a worker updates the wrong container's location.
//  * THE OFFSITE CASE IS RENDERED, NOT SWALLOWED. §3.5: a unit scanned at a
//    facility other than the one the system thinks it is at NEVER auto-moves —
//    it raises the `unit_found_offsite` exception. The response says so by
//    `location_updated: false` with a resolved location in a DIFFERENT facility
//    than the unit, and the screen states exactly that: nothing moved, the
//    discrepancy has been raised. A green tick there would be a lie the ledger
//    does not support.
//  * §9.15 damaged label — the manual serial field is always present (ScanInput).

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FloorButton, FloorError, FloorNotSaved, FloorScreen } from "../components/floor-chrome";
import { ScanInput } from "../components/scan-input";
import { useFloorOutbox, useFloorSubmit } from "../hooks/use-floor-outbox";
import { useOnline } from "../hooks/use-online";
import {
  deviceId,
  deviceIdIsStable,
  setStationLocationId,
  stationLocationId,
} from "../lib/floor-device";
import { floorFeedback } from "../lib/floor-feedback";
import { itemLabel, type OutboxItem } from "../lib/floor-outbox";
import type { ScanRequest, ScanResponse } from "../lib/floor-ops";
import { useInventoryPermissions } from "../../office/inventory/hooks/permissions";
import { formatMeasure, formatMeasureWithUom } from "../../office/inventory/lib/measure";
import { floorFailureMessage } from "../lib/floor-errors";

type ScanAction = ScanRequest["action"];

export function FloorScanScreen() {
  const { t, i18n } = useTranslation();
  const perms = useInventoryPermissions();
  const online = useOnline();
  const outbox = useFloorOutbox();
  const { state, submit, reset } = useFloorSubmit();
  const [action, setAction] = useState<ScanAction>("locate");
  const [measure, setMeasure] = useState("");
  const [measureError, setMeasureError] = useState<string | undefined>(undefined);
  const [station, setStation] = useState<string | undefined>(() => stationLocationId());

  async function scan(payload: string): Promise<void> {
    const entered = measure.trim();
    if (entered.length > 0 && !/^\d{1,12}(\.\d{1,6})?$/u.test(entered)) {
      // The contract's own `numeric(18,6)` pattern, checked before queueing:
      // a body the server will 422 must never sit in the queue for 24 hours.
      setMeasureError(t("inventory.floor.scan.measureInvalid"));
      floorFeedback("error");
      return;
    }
    setMeasureError(undefined);
    const body: ScanRequest = {
      scanned_payload: payload,
      // The operator's CHOSEN action, always. A measured length is an extra
      // fact, never a change of mode: the server applies attribution and the
      // re-measure independently in the same transaction (scan.service §3.5),
      // so silently rewriting "Putting it away" to "remeasure" — as this screen
      // used to — dropped the put-away with nothing on screen to say so.
      action,
      device_id: deviceId(),
      app_area: "floor",
      ...(station !== undefined ? { context_location_id: station } : {}),
      ...(entered.length > 0 ? { entered_measure: entered } : {}),
    };
    const settled = await submit({ kind: "scan", body });
    if (settled === undefined) floorFeedback("error");
    else if (settled.outcome === "applied") floorFeedback("ok");
    else if (settled.outcome === "needs_attention" || settled.outcome === "conflicted")
      // Conflicted buzzes like a failure, not like "queued" (the op-run rule):
      // the server REFUSED the fact — the one thing the haptic must not say
      // is "fine, it will go through".
      floorFeedback("error");
    else floorFeedback("warn");
    setMeasure("");
  }

  const settlement = state.phase === "settled" ? state.settlement : undefined;
  const applied =
    settlement?.outcome === "applied" ? (settlement.response as ScanResponse) : undefined;
  const recent = outbox.items.slice(-6).reverse();

  return (
    <FloorScreen
      title={t("inventory.floor.scan.title")}
      back={
        <Link to="/floor" className="floor-back">
          {t("inventory.floor.back")}
        </Link>
      }
    >
      {!perms.has("inventory.scan.locate") && perms.isResolved && !perms.isFeedError ? (
        <p className="floor-hint floor-hint-warn" data-testid="floor-scan-nopermission">
          {t("inventory.floor.scan.noPermission")}
        </p>
      ) : null}

      <fieldset className="floor-actions-radio">
        <legend>{t("inventory.floor.scan.actionLegend")}</legend>
        {(["locate", "lookup", "count", "receive"] as const).map((value) => (
          <label key={value} className="floor-radio">
            <input
              type="radio"
              name="scan-action"
              value={value}
              checked={action === value}
              onChange={() => {
                setAction(value);
              }}
            />
            <span>{t(`inventory.floor.scan.action.${value}`)}</span>
          </label>
        ))}
      </fieldset>

      <ScanInput
        onScan={(payload) => {
          void scan(payload);
        }}
        label={t("inventory.floor.scan.inputLabel")}
        placeholder={t("inventory.floor.scan.inputPlaceholder")}
        busy={state.phase === "submitting"}
        captureWedge
      />

      <div className="floor-field">
        <label htmlFor="floor-measure">{t("inventory.floor.scan.measureLabel")}</label>
        <input
          id="floor-measure"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={measure}
          placeholder={t("inventory.floor.scan.measurePlaceholder")}
          onChange={(event) => {
            setMeasure(event.target.value);
            setMeasureError(undefined);
          }}
          data-testid="floor-scan-measure"
        />
        <p className="floor-hint">{t("inventory.floor.scan.measureHint")}</p>
        {measureError !== undefined ? (
          <p className="floor-scan-error" role="alert" data-testid="floor-measure-error">
            {measureError}
          </p>
        ) : null}
      </div>

      {!deviceIdIsStable() ? (
        <p className="floor-hint floor-hint-warn" data-testid="floor-device-unstable">
          {t("inventory.floor.scan.deviceUnstable")}
        </p>
      ) : null}

      {/* §3.5 rung 2 — the station binding. It used to be a permanent notice
          naming a fix the operator had no way to perform (nothing in the app
          called `setStationLocationId`), which is worse than no notice: it
          teaches them to ignore the strip where the sync state also lives. The
          binding is now one motion — scan a bin, tap "use this" — with no
          picker and no typing. */}
      {station === undefined ? (
        <p className="floor-hint" data-testid="floor-no-station">
          {t("inventory.floor.scan.noStation")}
        </p>
      ) : (
        <p className="floor-hint" data-testid="floor-station-bound">
          {t("inventory.floor.scan.stationBound")}{" "}
          <button
            type="button"
            className="floor-linkbtn"
            onClick={() => {
              setStationLocationId(undefined);
              setStation(undefined);
            }}
            data-testid="floor-station-unbind"
          >
            {t("inventory.floor.scan.stationUnbind")}
          </button>
        </p>
      )}

      {state.phase === "submitting" ? (
        <p className="floor-hint" role="status">
          {t("inventory.floor.scan.sending")}
        </p>
      ) : null}

      {settlement?.outcome === "queued" ? (
        <div
          className="floor-result-card floor-result-queued"
          role="status"
          data-testid="scan-queued"
        >
          <p className="floor-result-title">{t("inventory.floor.scan.queuedTitle")}</p>
          <p className="floor-result-body">
            {online
              ? t("inventory.floor.scan.queuedSlowBody")
              : t("inventory.floor.scan.queuedOfflineBody")}
          </p>
        </div>
      ) : null}

      {settlement?.outcome === "needs_attention" ? (
        <FloorError
          title={t("inventory.floor.scan.failedTitle")}
          detail={floorFailureMessage(t, settlement.failure)}
          requestId={settlement.failure.requestId}
          onRetry={reset}
        />
      ) : null}

      {settlement?.outcome === "conflicted" ? (
        // The D-080 / spec 18 §3.3 outcome: the server ANSWERED and refused
        // the fact into the office conflict queue; the record sits on this
        // device as needs_review. The §4.2 passive note — never success and
        // never silence: this branch rendered NOTHING until the settlement
        // construction caught it, so a refused scan cleared the screen exactly
        // like an applied one. Copy is the tray's established review copy
        // (reason table included), so this screen and the tray the record now
        // lives in tell one story.
        <div
          className="floor-result-card floor-result-warn"
          role="alert"
          data-testid="scan-conflicted"
        >
          <p className="floor-result-title">{t("inventory.floor.tray.reviewHead")}</p>
          <p className="floor-result-body">
            {t(`inventory.floor.tray.reviewReasons.${settlement.reason}`, {
              defaultValue: t("inventory.floor.tray.reviewReasonFallback", {
                reason: settlement.reason,
              }),
            })}
          </p>
          <p className="floor-result-body">{t("inventory.floor.tray.reviewBody")}</p>
        </div>
      ) : null}

      {state.phase === "not_saved" ? <FloorNotSaved onRetry={reset} /> : null}

      {applied !== undefined ? (
        <ScanResult
          result={applied}
          locale={i18n.language}
          station={station}
          onBindStation={(locationId) => {
            setStationLocationId(locationId);
            setStation(locationId);
          }}
        />
      ) : null}

      {settlement !== undefined ? (
        <FloorButton onClick={reset} variant="ghost" testId="scan-clear">
          {t("inventory.floor.scan.clear")}
        </FloorButton>
      ) : null}

      {/* "Done here" — every WMS handheld keeps a visible session tally so the
          operator can self-verify before walking away from an aisle. Eight
          scans in a dead zone must not collapse into one card and a number. */}
      {recent.length > 0 ? (
        <section className="floor-panel" data-testid="floor-session-list">
          <h2 className="floor-panel-head">{t("inventory.floor.scan.doneHere")}</h2>
          <ul className="floor-session">
            {recent.map((item) => (
              <SessionRow key={item.clientKey} item={item} />
            ))}
          </ul>
        </section>
      ) : null}
    </FloorScreen>
  );
}

function SessionRow({ item }: { item: OutboxItem }) {
  const { t } = useTranslation();
  const label = itemLabel(item);
  return (
    <li className="floor-session-row" data-testid="floor-session-row">
      <span>{item.identity ?? t(label.key, label.params)}</span>
      <span className="floor-session-state">
        {/* The queue is ONE queue (D-048/D-054), so this list can hold a
            needs_review record another screen's tap left behind. "Waiting to
            send" would be false for it — it will never be sent (D-080); it is
            with the office. Found by the settlement construction. */}
        {item.state === "needs_attention"
          ? t("inventory.floor.scan.sessionNeedsYou")
          : item.state === "needs_review"
            ? t("inventory.floor.tray.reviewHead")
            : t("inventory.floor.scan.sessionWaiting")}
      </span>
    </li>
  );
}

/**
 * The four outcomes §3.5 can produce, each rendered as itself.
 *
 * `unknown` is not an error state: §9.9 says a foreign tenant's QR resolves to
 * `unknown`, reveals nothing, and still writes a scan_events row. So it renders
 * as a recorded, unrecognised label — with the §4.2 "register it?" pointer for
 * storekeepers — rather than as a failure the operator should re-scan.
 */
function ScanResult({
  result,
  locale,
  station,
  onBindStation,
}: {
  result: ScanResponse;
  locale: string;
  station: string | undefined;
  onBindStation: (locationId: string) => void;
}) {
  const { t } = useTranslation();

  if (result.resolved_kind === "unknown") {
    return (
      <div className="floor-result-card floor-result-warn" role="status" data-testid="scan-unknown">
        <p className="floor-result-title">{t("inventory.floor.scan.unknownTitle")}</p>
        <p className="floor-result-body">{t("inventory.floor.scan.unknownBody")}</p>
      </div>
    );
  }

  if (result.resolved_kind === "location") {
    const location = result.location ?? undefined;
    return (
      <div className="floor-result-card floor-result-ok" role="status" data-testid="scan-location">
        <p className="floor-result-title">{location?.path ?? location?.code ?? ""}</p>
        <p className="floor-result-body">{t("inventory.floor.scan.locationBody")}</p>
        {location != null && station !== location.id ? (
          <FloorButton
            variant="ghost"
            onClick={() => {
              onBindStation(location.id);
            }}
            testId="floor-station-bind"
          >
            {t("inventory.floor.scan.stationBind")}
          </FloorButton>
        ) : null}
      </div>
    );
  }

  const unit = result.stock_unit ?? undefined;
  const location = result.location ?? undefined;
  // `remeasure` is `nullable().optional()` on the wire (§5 row 20), and JSON
  // carries NULL, never undefined — so an `!== undefined` guard let `null`
  // through and the next line read `null.adjusted`, blanking the whole result
  // card behind the error boundary. That is every scan WITHOUT a measured
  // length, i.e. the common case (D-055 pt 10b).
  const remeasure = result.remeasure ?? undefined;
  // §3.5 / §9.3: a unit resolved against a location in ANOTHER facility never
  // auto-moves — the discrepancy is raised as `unit_found_offsite`.
  const offsite =
    unit !== undefined &&
    location !== undefined &&
    location.facility_id !== unit.facility_id &&
    !result.location_updated;

  return (
    <div
      className={`floor-result-card ${offsite ? "floor-result-warn" : "floor-result-ok"}`}
      role="status"
      data-testid="scan-unit"
    >
      <p className="floor-result-title">
        {unit?.serial_code ?? t("inventory.floor.scan.unitTitleFallback")}
      </p>

      {unit !== undefined ? (
        <p className="floor-result-measure">
          {formatMeasureWithUom(unit.remaining_measure, unit.measure_uom, { locale })}
        </p>
      ) : null}

      {offsite ? (
        <p className="floor-result-body floor-result-offsite" data-testid="scan-offsite">
          {t("inventory.floor.scan.offsiteBody", {
            here: location.path,
          })}
        </p>
      ) : result.location_updated ? (
        <p className="floor-result-body" data-testid="scan-moved">
          {t("inventory.floor.scan.movedBody", {
            path: location?.path ?? "",
          })}
        </p>
      ) : (
        <p className="floor-result-body" data-testid="scan-not-moved">
          {t("inventory.floor.scan.notMovedBody")}
        </p>
      )}

      {remeasure !== undefined ? (
        <p className="floor-result-body" data-testid="scan-remeasure">
          {/* DERIVED, both numbers: the wire is canonical numeric(18,6)
              ("-2.000000"), and the measure line two elements up already
              renders through the shared formatter — one sentence must not
              carry two shapes of one number (the my-shift 420.000 class). */}
          {remeasure.adjusted
            ? t("inventory.floor.scan.remeasureAdjusted", {
                delta: formatMeasure(remeasure.delta, { locale }),
                remaining: formatMeasure(remeasure.remaining_measure, { locale }),
              })
            : t("inventory.floor.scan.remeasureWithinTolerance", {
                remaining: formatMeasure(remeasure.remaining_measure, { locale }),
              })}
        </p>
      ) : null}

      {unit !== undefined ? (
        <Link
          to="/floor/units/$id"
          params={{ id: unit.id }}
          className="floor-go floor-go-lead"
          data-testid="scan-open-unit"
        >
          {t("inventory.floor.scan.openUnit")}
        </Link>
      ) : null}
    </div>
  );
}
